/**
 * AuditReadiness — 0-100 IRD-audit-readiness score per client with an
 * inline missing-docs list.
 *
 * Two-pane layout: left = filterable client list sorted lowest-score
 * first, right = expanded detail (10 factor breakdown + missing docs list
 * + jump-into-client link). Deterministic scoring only — no LLM.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCcw, ShieldCheck, ShieldAlert, ShieldX, ExternalLink, ChevronRight, CheckCircle2, XCircle, FileWarning } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const TONES = {
    green:  { chip: "bg-emerald-100 text-emerald-800 border-emerald-200", bar: "bg-emerald-500",  Icon: ShieldCheck, label: "Ready" },
    amber:  { chip: "bg-amber-100 text-amber-900 border-amber-200",       bar: "bg-amber-500",   Icon: ShieldAlert, label: "Needs work" },
    red:    { chip: "bg-red-100 text-red-800 border-red-200",             bar: "bg-red-500",     Icon: ShieldX,     label: "Not ready" },
};

function ScoreDial({ score, tone }) {
    const t = TONES[tone] || TONES.red;
    return (
        <div className="flex items-center gap-2">
            <div className="relative h-11 w-11 shrink-0">
                <svg viewBox="0 0 36 36" className="h-11 w-11 -rotate-90">
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#EEEDE7" strokeWidth="3" />
                    <circle
                        cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" strokeLinecap="round"
                        className={t.bar.replace("bg-", "text-").replace("-500", "-600")}
                        stroke="currentColor"
                        strokeDasharray={`${(score / 100) * 97.4} 97.4`}
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-[12px] font-semibold tabular-nums">
                    {score}
                </div>
            </div>
        </div>
    );
}

export default function AuditReadiness() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [tone, setTone] = useState("all");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);   // client_id
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (tone !== "all") params.set("tone", tone);
            if (q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/tax/audit-readiness/?${params.toString()}`);
            setRows(data.rows || []);
            setSummary(data.summary || {});
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [tone, q]);

    useEffect(() => { load(); }, [load]);

    const loadDetail = useCallback(async (cid) => {
        setDetailLoading(true);
        setDetail(null);
        try {
            const { data } = await api.get(`/tax/audit-readiness/${cid}`);
            setDetail(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDetailLoading(false); }
    }, []);

    useEffect(() => {
        if (selected) loadDetail(selected);
        else setDetail(null);
    }, [selected, loadDetail]);

    useEffect(() => {
        // Auto-pick the first (worst) row once the list loads.
        if (!selected && rows.length > 0) setSelected(rows[0].client_id);
    }, [rows, selected]);

    return (
        <div className="space-y-4">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-semibold text-[#0F1115] tracking-tight">Audit readiness</h1>
                    <p className="text-sm text-[#5E636E] mt-0.5">
                        How ready every client's file is if IRD comes knocking. Lowest scores first — pick a client to see exactly what's missing.
                    </p>
                </div>
                <Button variant="outline" onClick={load} disabled={loading} data-testid="aud-refresh">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCcw className="h-4 w-4 mr-1" /> Refresh</>}
                </Button>
            </header>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Clients scored" value={summary.total || 0} tone="text-[#0F1115] bg-white border-[#E4E4E7]" />
                <Kpi label="Average score" value={summary.avg_score ?? "—"} tone="text-slate-800 bg-slate-50 border-slate-200" suffix="/100" />
                <ToneKpi label="Not ready" tone="red" count={summary.red || 0} activeTone={tone} onClick={setTone} />
                <ToneKpi label="Ready" tone="green" count={summary.green || 0} activeTone={tone} onClick={setTone} />
            </div>

            <Card className="p-3 flex flex-wrap items-center gap-2">
                <div className="flex gap-1" data-testid="aud-tone-tabs">
                    {["all", "red", "amber", "green"].map((k) => (
                        <button
                            key={k}
                            onClick={() => setTone(k)}
                            className={`h-8 rounded-full border px-3 text-[12.5px] capitalize ${tone === k ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:bg-zinc-50"}`}
                            data-testid={`aud-tone-${k}`}
                        >
                            {k === "all" ? "All" : k === "red" ? "Not ready" : k === "amber" ? "Needs work" : "Ready"}
                        </button>
                    ))}
                </div>
                <Input
                    placeholder="Search client…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="max-w-xs h-8 ml-auto"
                    data-testid="aud-search"
                />
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                <Card className="p-0 overflow-hidden lg:col-span-2 max-h-[70vh] flex flex-col">
                    <div className="border-b border-[#EEEDE7] bg-[#FAFAF7] px-3 py-2 text-[11.5px] uppercase tracking-wider text-[#5E636E]">
                        Clients · lowest first
                    </div>
                    <div className="flex-1 overflow-y-auto" data-testid="aud-list">
                        {loading ? (
                            <div className="p-8 text-center text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Scoring…</div>
                        ) : rows.length === 0 ? (
                            <div className="p-8 text-center text-sm text-[#5E636E]" data-testid="aud-empty">No clients match these filters.</div>
                        ) : (
                            <ul className="divide-y divide-[#EEEDE7]">
                                {rows.map((r) => (
                                    <li key={r.client_id}>
                                        <button
                                            type="button"
                                            onClick={() => setSelected(r.client_id)}
                                            className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-zinc-50 ${selected === r.client_id ? "bg-[#FAFAF7]" : ""}`}
                                            data-testid={`aud-row-${r.client_id}`}
                                        >
                                            <ScoreDial score={r.score} tone={r.tone} />
                                            <div className="min-w-0 flex-1">
                                                <div className="text-[13.5px] font-medium text-[#0F1115] truncate">{r.client_name || "—"}</div>
                                                <div className="text-[11.5px] text-[#5E636E] truncate">
                                                    {r.missing_count === 0 ? "Fully ready" : `${r.missing_count} missing · ${r.top_missing[0] || ""}`}
                                                </div>
                                            </div>
                                            <ChevronRight className="h-4 w-4 text-[#5E636E]" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Card>

                <Card className="p-0 overflow-hidden lg:col-span-3">
                    {detailLoading ? (
                        <div className="p-12 text-center text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading breakdown…</div>
                    ) : !detail ? (
                        <div className="p-12 text-center text-sm text-[#5E636E]" data-testid="aud-empty-detail">Pick a client on the left to see their score breakdown.</div>
                    ) : (
                        <DetailPanel detail={detail} />
                    )}
                </Card>
            </div>
        </div>
    );
}

function Kpi({ label, value, tone, suffix }) {
    return (
        <div className={`rounded-xl border p-3 ${tone}`}>
            <div className="text-[11px] uppercase tracking-wider opacity-80">{label}</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">
                {value}{suffix ? <span className="text-[11px] opacity-70 ml-0.5">{suffix}</span> : null}
            </div>
        </div>
    );
}

function ToneKpi({ label, tone, count, activeTone, onClick }) {
    const t = TONES[tone];
    const active = activeTone === tone;
    return (
        <button
            type="button"
            onClick={() => onClick(active ? "all" : tone)}
            className={`rounded-xl border p-3 text-left transition ${t.chip} ${active ? "ring-2 ring-offset-1 ring-[#0F1115]" : "hover:shadow-sm"}`}
            data-testid={`aud-kpi-${tone}`}
        >
            <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider opacity-80">{label}</span>
                <t.Icon className="h-4 w-4 opacity-70" />
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">{count}</div>
        </button>
    );
}

function DetailPanel({ detail }) {
    const t = TONES[detail.tone] || TONES.red;
    const okFactors = detail.breakdown.filter((b) => b.ok);
    const missingFactors = detail.breakdown.filter((b) => !b.ok);
    return (
        <div className="p-5 space-y-5" data-testid={`aud-detail-${detail.client_id}`}>
            <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Tax year {detail.tax_year}</div>
                    <h2 className="mt-0.5 text-xl font-semibold text-[#0F1115] truncate">{detail.client_name}</h2>
                    <div className="mt-2 flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${t.chip}`}>
                            <t.Icon className="h-3 w-3" /> {t.label}
                        </span>
                        <span className="text-[12px] text-[#5E636E]">
                            {missingFactors.length ? `${missingFactors.length} of ${detail.breakdown.length} factors need work` : "All factors clean"}
                        </span>
                    </div>
                    <div className="mt-3 h-2.5 w-full rounded-full bg-[#EEEDE7] overflow-hidden">
                        <div className={`h-full rounded-full ${t.bar}`} style={{ width: `${detail.score}%` }} />
                    </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <div className="text-3xl font-semibold tabular-nums text-[#0F1115]">{detail.score}<span className="text-[13px] text-[#5E636E]">/100</span></div>
                    <Link to={`/app/clients/${detail.client_id}`} data-testid={`aud-open-client-${detail.client_id}`}>
                        <Button variant="outline" size="sm" className="h-8 text-[12px]"><ExternalLink className="h-3 w-3 mr-1" /> Open client</Button>
                    </Link>
                </div>
            </div>

            {missingFactors.length > 0 && (
                <section data-testid="aud-missing">
                    <div className="flex items-center gap-2 mb-2">
                        <FileWarning className="h-4 w-4 text-red-700" />
                        <h3 className="text-[13px] font-semibold text-[#0F1115]">Missing before IRD review</h3>
                        <span className="text-[11px] text-[#5E636E]">— {detail.missing_docs.length} item{detail.missing_docs.length === 1 ? "" : "s"}</span>
                    </div>
                    <ul className="rounded-lg border border-red-200 bg-red-50 divide-y divide-red-100">
                        {detail.missing_docs.map((m, i) => (
                            <li key={i} className="px-3 py-2 text-[12.5px] text-[#78511A] flex items-start gap-2" data-testid={`aud-miss-${i}`}>
                                <XCircle className="h-3.5 w-3.5 text-red-600 mt-0.5 shrink-0" />
                                <span>{m}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section>
                <h3 className="text-[13px] font-semibold text-[#0F1115] mb-2">Factor breakdown</h3>
                <ul className="rounded-lg border border-[#EEEDE7] divide-y divide-[#EEEDE7]">
                    {detail.breakdown.map((b) => (
                        <li key={b.key} className="px-3 py-2 flex items-center gap-3" data-testid={`aud-factor-${b.key}`}>
                            {b.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] text-[#0F1115]">{b.label}</div>
                                {b.note && <div className="text-[11.5px] text-[#5E636E] truncate">{b.note}</div>}
                            </div>
                            <div className="text-[12px] tabular-nums font-medium text-[#0F1115]">
                                {b.earned}<span className="text-[#5E636E]">/{b.weight}</span>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            {okFactors.length === detail.breakdown.length && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-900 flex items-center gap-2" data-testid="aud-all-clean">
                    <CheckCircle2 className="h-4 w-4" /> This file is IRD-review ready.
                </div>
            )}
        </div>
    );
}
